import { of, tap } from "rxjs";
import { inject, Injectable } from "@angular/core";

import {
  AuthService,
  Dropdown,
  GetPagedBody,
  HttpService,
  HttpServiceBaseCRUD,
  Lookup,
  SchoolUniversityVisitsVisitorCategory,
  VisitorCategory,
} from "src/app/core";

@Injectable({
  providedIn: "root",
})
export class VisitorCategoriesService extends HttpService implements HttpServiceBaseCRUD {
  readonly #authService = inject(AuthService);

  protected override get baseUrl(): string {
    return "visitorcategories/";
  }

  get subCategoryProperties() {
    return [
      "adults",
      "children",
      "students",
      "retired",
      "seniorCitizen",
      "seniorGCCNationals",
      "seniorResident",
      "socialSecurity",
      "specialNeedsPeople",
      "guestsOfTheDiwan",
      "icomOrICOMOSMember",
      "licensedTourGuide",
      "receptionStaffInLicensedHotels",
      "sultanatesGuestsAndExcellencies",
      "journalistDigitalMedia",
      "omaniMuseumsEmployees",
      "museumFriends",
      "escortsOfOfficialVisits",
    ];
  }

  /**
   * @description based on these sub categories the free visitor will display the `bring approval` note.
   */
  get freeSubCategories() {
    return [
      "seniorCitizen",
      "seniorGCCNationals",
      "seniorResident",
      "students",
      "omaniMuseumsEmployees",
      "specialNeedsPeople",
      "receptionStaffInLicensedHotels",
      "icomOrICOMOSMember",
      "retired",
      "socialSecurity",
      "licensedTourGuide",
    ];
  }

  get reservationVisitorCategories(): VisitorCategory {
    return {
      Omani: {
        adults: 0,
        children: 0,
        students: 0,
        retired: 0,
        seniorCitizen: 0,
        socialSecurity: 0,
        specialNeedsPeople: 0,
      },
      GCCCitizens: {
        adults: 0,
        children: 0,
        students: 0,
        seniorCitizen: 0,
        specialNeedsPeople: 0,
      },
      Resident: {
        adults: 0,
        children: 0,
        students: 0,
        retired: 0,
        seniorResident: 0,
        specialNeedsPeople: 0,
      },
      Tourist: {
        adults: 0,
        children: 0,
        students: 0,
        seniorCitizen: 0,
        specialNeedsPeople: 0,
      },
    };
  }

  get reservationEntranceTicketVisitorCategories() {
    return {
      Omani: {
        adults: 0,
        children: 0,
        students: 0,
        retired: 0,
        seniorCitizen: 0,
        socialSecurity: 0,
        specialNeedsPeople: 0,
      },
      GCCCitizens: {
        adults: 0,
        children: 0,
        students: 0,
        seniorCitizen: 0,
        specialNeedsPeople: 0,
      },
      Resident: {
        adults: 0,
        children: 0,
        students: 0,
        seniorResident: 0,
        specialNeedsPeople: 0,
      },
      Tourist: {
        adults: 0,
        children: 0,
        students: 0,
        specialNeedsPeople: 0,
      },
    };
  }

  get freeReservationVisitorCategories(): VisitorCategory<true> {
    return {
      Omani: {
        children: 0,
        students: 0,
        retired: 0,
        seniorCitizen: 0,
        socialSecurity: 0,
        specialNeedsPeople: 0,
        guestsOfTheDiwan: 0,
        icomOrICOMOSMember: 0,
        licensedTourGuide: 0,
        museumFriends: 0,
        receptionStaffInLicensedHotels: 0,
        sultanatesGuestsAndExcellencies: 0,
        omaniMuseumsEmployees: 0,
        journalistDigitalMedia: 0,
      },
      GCCCitizens: {
        children: 0,
        students: 0,
        seniorCitizen: 0,
        specialNeedsPeople: 0,
        guestsOfTheDiwan: 0,
        icomOrICOMOSMember: 0,
        licensedTourGuide: 0,
        museumFriends: 0,
        receptionStaffInLicensedHotels: 0,
        sultanatesGuestsAndExcellencies: 0,
        omaniMuseumsEmployees: 0,
        journalistDigitalMedia: 0,
      },
      Resident: {
        children: 0,
        students: 0,
        seniorResident: 0,
        specialNeedsPeople: 0,
        icomOrICOMOSMember: 0,
        licensedTourGuide: 0,
        museumFriends: 0,
        guestsOfTheDiwan: 0,
        receptionStaffInLicensedHotels: 0,
        sultanatesGuestsAndExcellencies: 0,
        journalistDigitalMedia: 0,
      },
      Tourist: {
        children: 0,
        students: 0,
        specialNeedsPeople: 0,
        journalistDigitalMedia: 0,
      },
    };
  }

  get schoolUniversityVisitsVisitorCategories(): SchoolUniversityVisitsVisitorCategory {
    return {
      Omani: {
        students: 0,
      },
    };
  }

  get newReservationVisitorCategories() {
    return {
      Omani: {
        adults: 0,
      },
      GCCCitizens: {
        adults: 0,
      },
      Resident: {
        adults: 0,
      },
      Tourist: {
        adults: 0,
      },
      Free: {
        seniorCitizen: 0,
        seniorGCCNationals: 0,
        seniorResident: 0,
        children: 0,
        students: 0,
        museumFriends: 0,
        omaniMuseumsEmployees: 0,
        specialNeedsPeople: 0,
        receptionStaffInLicensedHotels: 0,
        icomOrICOMOSMember: 0,
        retired: 0,
        socialSecurity: 0,
        licensedTourGuide: 0,
        guestsOfTheDiwan: 0,
        sultanatesGuestsAndExcellencies: 0,
        journalistDigitalMedia: 0,
        escortsOfOfficialVisits: 0,
      },
    };
  }

  get freeVisitorSubCategories(): Dropdown[] {
    return Object.keys(this.newReservationVisitorCategories.Free)
      .filter((key) => {
        if (key === "museumFriends" && !this.#authService.isGuest())
          return this.#authService.isMembershipUser() || this.#authService.isCompanyMembershipUser();
        return true;
      })
      .map((key) => ({
        id: `${key}Number`,
        nameEn: `VISITOR_CATEGORY.${key.toUpperCase()}`,
        nameAr: `VISITOR_CATEGORY.${key.toUpperCase()}`,
        code: `${key}Number`,
      }));
  }

  visitorCategories: Lookup[] = [];

  add(body: Lookup) {
    return this.post<Lookup>({ APIName: "add", body, showAlert: true });
  }

  update(body: Lookup) {
    return this.put<Lookup>({ APIName: "update", body, showAlert: true });
  }

  remove(id: string) {
    return this.delete<boolean>({ APIName: `deletesoft/${id}`, showAlert: true });
  }

  getById(id: string) {
    return this.get<Lookup>({ APIName: `get-id/${id}` });
  }

  getPaged(body: GetPagedBody<Lookup>) {
    return this.getPagedRequest<Lookup[]>({ APIName: "getpaged", body });
  }

  getAll() {
    if (this.visitorCategories.length) return of(this.visitorCategories);
    return this.get<Lookup[]>({ APIName: "getall" }).pipe(tap((visitorCategories) => (this.visitorCategories = visitorCategories)));
  }
}
